import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CareerTimeline = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const timelineData = [
  {
    year: "2023",
    period: "2026",
    role: "Computer Science Student",
    company: "University of Bolton",
  companyLogo: "https://tse4.mm.bing.net/th/id/OIP.V543by55E9-aYWG64len-gAAAA?rs=1&pid=ImgDetMain&o=7&rm=3",
    companyLogoAlt: "University of Bolton logo",
    type: "Education",
    location: "Bolton, UK",
    description: "Pursuing BSc (Hons) Computer Science with a focus on software engineering, cloud computing, and AI. Active participant in university tech events and coding competitions.",
    achievements: [
    "Consistently high academic performance",
    "Active member of the Computer Science Society",
    "Participated in national hackathons",
    "Volunteered for university open days"],

    technologies: ["Python", "JavaScript", "Java", "Cloud Computing", "AI", "Software Engineering"],
    highlights: "Expected graduation in 2026"
  }
 ];


  return (
    <section className="py-20 lg:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Clock" size={16} />
            <span>Professional Journey</span>
          </div>
          
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            Career Timeline &
            <span className="text-gradient-brand block">Growth Milestones</span>
          </h2>
          
          <p className="text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            From curious student to senior developer—here's how I've grown, learned, and contributed 
            to the tech industry over the years.
          </p>
        </div>

        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-4 lg:left-1/2 lg:transform lg:-translate-x-0.5 top-0 bottom-0 w-0.5 bg-border"></div>

          {/* Timeline Items */}
          <div className="space-y-12">
            {timelineData?.map((item, index) => (
            <div
              key={index}
              className={`relative flex flex-col lg:flex-row lg:items-center ${
              index % 2 === 0 ? 'lg:flex-row-reverse' : ''}`
              }>

                {/* Timeline Dot */}
                <div className="absolute left-4 lg:left-1/2 lg:transform lg:-translate-x-1/2 w-8 h-8 bg-primary rounded-full border-4 border-background shadow-brand-sm flex items-center justify-center z-10">
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                </div>

                {/* Content Card */}
                <div className={`ml-16 lg:ml-0 lg:w-5/12 ${index % 2 === 0 ? 'lg:mr-auto lg:pr-8' : 'lg:ml-auto lg:pl-8'}`}>
                  <div
                  className={`bg-card border border-border rounded-2xl p-6 lg:p-8 shadow-brand-sm hover-lift transition-brand cursor-pointer ${
                  activeIndex === index ? 'ring-2 ring-primary/20 shadow-brand' : ''}`
                  }
                  onClick={() => setActiveIndex(activeIndex === index ? -1 : index)}>

                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 rounded-xl overflow-hidden border border-border">
                          <Image
                          src={item?.companyLogo}
                          alt={item?.companyLogoAlt}
                          className="w-full h-full object-cover" />

                        </div>
                        <div>
                          <div className="text-sm font-medium text-primary">
                            {item?.year} - {item?.period}
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center space-x-2">
                            <span>{item?.type}</span>
                            <span>•</span>
                            <span>{item?.location}</span>
                          </div>
                        </div>
                      </div>
                      
                      <Icon
                      name={activeIndex === index ? "ChevronUp" : "ChevronDown"}
                      size={20}
                      className="text-muted-foreground" />

                    </div>

                    {/* Role & Company */}
                    <div className="mb-4">
                      <h3 className="text-xl font-bold text-card-foreground mb-1">
                        {item?.role}
                      </h3>
                      <p className="text-lg text-accent font-medium">
                        {item?.company}
                      </p>
                    </div>

                    {/* Description */}
                    <p className="text-muted-foreground mb-4 leading-relaxed">
                      {item?.description}
                    </p>

                    {/* Highlight Badge */}
                    <div className="inline-flex items-center space-x-2 bg-success/10 text-success px-3 py-1 rounded-full text-sm font-medium mb-4">
                      <Icon name="Star" size={14} />
                      <span>{item?.highlights}</span>
                    </div>

                    {/* Expanded Content */}
                    {activeIndex === index && (
                  <div className="space-y-6 pt-4 border-t border-border">
                        {/* Key Achievements */}
                        <div>
                          <h4 className="text-lg font-semibold text-card-foreground mb-3 flex items-center space-x-2">
                            <Icon name="Trophy" size={18} className="text-warning" />
                            <span>Key Achievements</span>
                          </h4>
                          <ul className="space-y-2">
                            {item?.achievements?.map((achievement, idx) => (
                        <li key={idx} className="flex items-start space-x-2 text-muted-foreground">
                                <Icon name="CheckCircle" size={16} className="text-success mt-0.5 flex-shrink-0" />
                                <span>{achievement}</span>
                              </li>
                        ))}
                          </ul>
                        </div>

                        {/* Technologies */}
                        <div>
                          <h4 className="text-lg font-semibold text-card-foreground mb-3 flex items-center space-x-2">
                            <Icon name="Code2" size={18} className="text-primary" />
                            <span>Technologies Used</span>
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {item?.technologies?.map((tech, idx) => (
                        <span
                          key={idx}
                          className="bg-muted text-muted-foreground px-3 py-1 rounded-full text-sm font-medium">

                                {tech}
                              </span>
                        ))}
                          </div>
                        </div>
                      </div>
                  )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-20 bg-gradient-to-r from-primary/5 via-accent/5 to-success/5 rounded-3xl p-8 lg:p-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              Journey Highlights
            </h3>
            <p className="text-muted-foreground">
              Key milestones and achievements throughout my career
            </p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl lg:text-4xl font-bold text-primary mb-2">8+</div>
              <div className="text-sm text-muted-foreground">Years Experience</div>
            </div>
            <div className="text-center">
              <div className="text-3xl lg:text-4xl font-bold text-accent mb-2">5</div>
              <div className="text-sm text-muted-foreground">Companies</div>
            </div>
            <div className="text-center">
              <div className="text-3xl lg:text-4xl font-bold text-success mb-2">150+</div>
              <div className="text-sm text-muted-foreground">Projects</div>
            </div>
            <div className="text-center">
              <div className="text-3xl lg:text-4xl font-bold text-warning mb-2">15+</div>
              <div className="text-sm text-muted-foreground">Technologies</div>
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default CareerTimeline;
